// Сумма выигрыша на экране: её запись и её набор золотыми цифрами.
//
// Цифра — это кадр листа шрифта, а не буква, поэтому строка набирается спрайтами,
// см. WIN_FONT_SHEET. Спрайты живут пулом: за время счёта строка меняется десятки раз.
//
// Начало координат узла стоит в середине записи. Поэтому сумму ставят один раз, а меняться
// по ширине она может как угодно: середина остаётся на месте.
//
// Сумму называют два места игры: показ выигрыша спина и завершение бонуса Steam Spin.
// Считают и рисуют они её одинаково, поэтому счёт и набор лежат здесь.

import { Container, Sprite, type Texture } from 'pixi.js';

import type { Cents } from '../api/gameConfig';
import { WIN_FONT_SHEET } from '../assets/catalog';
import type { AssetManager, TextureSheet } from '../assets/manager';

/** Знак валюты игры. Счёт идёт в евро, как на референсах в GDD, раздел 7.2. */
const CURRENCY: string = '€';

/**
 * Запись суммы для игрока. Счёт в игре идёт в центах, а на экран сумма выходит в единицах.
 *
 * Тысячи отделены запятой: со ставками счёт доходит до шести цифр, и сплошной ряд их
 * не прочитать. Запятая есть и в шрифте выигрыша, поэтому золотые цифры пишут сумму так же,
 * как её пишет полоса под машиной.
 */
export function amountText(cents: Cents): string {
  const units: string = Math.floor(Math.abs(cents) / 100).toString();
  const fraction: string = (Math.abs(cents) % 100).toString().padStart(2, '0');
  const sign: string = cents < 0 ? '-' : '';

  return `${sign}${grouped(units)}.${fraction}`;
}

/** Разбивает целую часть на тройки цифр справа налево. */
function grouped(units: string): string {
  let text: string = '';

  for (let index = 0; index < units.length; index++) {
    // Запятая встаёт перед каждой тройкой, кроме первой: у неё слева ничего нет.
    if (index > 0 && (units.length - index) % 3 === 0)
      text += ',';

    text += units[index];
  }

  return text;
}

/**
 * Запись суммы со знаком валюты.
 *
 * Её игра набирает обычным шрифтом: в шрифте выигрыша лежат только цифры и точка,
 * знака валюты в нём нет.
 */
export function moneyText(cents: Cents): string {
  return `${CURRENCY}${amountText(cents)}`;
}

/**
 * Сколько центов показать на счётчике.
 *
 * Счётчик идёт от нуля до всей суммы за одно и то же время, какой бы она ни была. Скорость
 * по величине выигрыша пойдёт вместе с крупным выигрышем: там она и нужна, чтобы отличить
 * большую выплату от малой.
 *
 * Дробных центов на экране не бывает, поэтому число округляется вниз.
 */
export function countedAmount(total: Cents, passed: number, seconds: number): Cents {
  if (passed >= seconds)
    return total;

  return Math.floor((total * Math.max(0, passed)) / seconds);
}

/** Сумма выигрыша, набранная цифрами шрифта. */
export class AmountLabel {
  readonly view: Container = new Container();

  readonly #font: TextureSheet<string>;
  readonly #height: number;

  /** Цифры записи. Лежат в своём узле: он и сдвигается на половину ширины. */
  readonly #digits: Container = new Container();
  readonly #sprites: Sprite[] = [];

  /** Сколько центов сейчас нарисовано. Минус один — не нарисовано ничего. */
  #drawn = -1;

  /** @param height Высота цифры в тех же координатах, в которых лежит узел. */
  constructor(assets: AssetManager, height: number) {
    this.#font = assets.sheet(WIN_FONT_SHEET);
    this.#height = height;

    this.view.addChild(this.#digits);
  }

  /**
   * Рисует сумму и ставит её серединой в начало координат узла.
   *
   * Строка перерисовывается, только когда счётчик дошёл до нового числа. Иначе за время счёта
   * работа шла бы на каждом кадре впустую.
   */
  draw(cents: Cents): void {
    if (cents === this.#drawn)
      return;

    this.#drawn = cents;

    const text: string = amountText(cents);
    const scale: number = this.#height / this.#glyph('0').height;

    let width = 0;
    for (let place = 0; place < text.length; place++) {
      const digit: Sprite = this.#digit(place, scale);
      const texture: Texture = this.#glyph(text[place]);

      digit.texture = texture;
      digit.x = width;
      digit.visible = true;

      width += texture.width * scale;
    }

    // Число укоротилось: лишние цифры пула уходят с экрана, но остаются в запасе.
    for (let extra = text.length; extra < this.#sprites.length; extra++)
      this.#sprites[extra].visible = false;

    this.#digits.x = -width / 2;
  }

  /** Забывает нарисованное: та же сумма после этого рисуется заново. */
  clear(): void {
    this.#drawn = -1;
  }

  /** Спрайт под заданное место строки. Пул растёт по мере надобности и живёт со сценой. */
  #digit(place: number, scale: number): Sprite {
    const ready: Sprite | undefined = this.#sprites[place];
    if (ready !== undefined)
      return ready;

    const digit = new Sprite();
    digit.anchor.set(0, 0.5);
    digit.scale.set(scale);

    this.#sprites.push(digit);
    this.#digits.addChild(digit);

    return digit;
  }

  /**
   * Кадр шрифта под символ записи.
   *
   * @throws Error если такого символа в шрифте нет
   */
  #glyph(character: string): Texture {
    const texture: Texture | undefined = this.#font[character];
    if (texture === undefined)
      throw new Error(`В шрифте выигрыша нет символа "${character}"`);

    return texture;
  }
}
